import type { components } from './schema'

/** The one error body of the API (MRS 9 error contract, `Error` schema). */
export type ErrorBody = components['schemas']['Error']

/** A `reason` code of a 409 response. */
export type ConflictReason = NonNullable<ErrorBody['reason']>

/**
 * Every reason code the spec knows, as a runtime list. `satisfies` refuses a
 * code the spec does not have; the `MissingReason` check below refuses a spec
 * code this list does not have. Each code has a message under
 * `errors.reason.*` in the locale file (tested in client.spec.ts).
 */
export const CONFLICT_REASONS = [
  'mfa_required',
  'account_banned',
  'invalid_signature',
  'company_active',
  'tickets_reference_product',
  'ai_runs_reference_repositories',
  'ai_runs_reference_repository',
  'primary_has_siblings',
  'only_repository_primary',
  'invalid_status_transition',
  'idempotency_key_reused',
  'idempotency_in_progress',
  'system_role_read_only',
  'role_in_use',
  'invite_not_pending',
  'mfa_mandatory_for_staff',
  'mfa_already_confirmed',
  'attachment_not_clean',
  'skill_in_use',
  'graduation_not_available',
  'suggestion_not_open',
  'run_not_failed',
  'report_not_failed',
  'suggestion_not_overridable',
  'article_published',
  'category_in_use',
  'video_host_unavailable',
  'live_updates_unavailable',
] as const satisfies readonly ConflictReason[]

type MissingReason = Exclude<ConflictReason, (typeof CONFLICT_REASONS)[number]>
// Fails `pnpm typecheck` when a regenerated schema carries a reason code that
// CONFLICT_REASONS, and so the locale file, does not know yet.
const everyReasonListed: [MissingReason] extends [never] ? true : never = true
void everyReasonListed

export function isConflictReason(value: unknown): value is ConflictReason {
  return typeof value === 'string' && (CONFLICT_REASONS as readonly string[]).includes(value)
}

/** Any non-2xx answer from the API. Subclasses cover the MRS 9 contract. */
export class ApiError extends Error {
  override readonly name: string = 'ApiError'

  readonly status: number

  readonly body: ErrorBody | null

  constructor(
    status: number,
    body: ErrorBody | null,
    message: string = body?.message ?? `The API answered ${status}.`,
  ) {
    super(message)
    this.status = status
    this.body = body
  }
}

/** 422: the input is invalid. `fieldErrors` is ready for `useForm.setFieldError`. */
export class ApiValidationError extends ApiError {
  override readonly name = 'ApiValidationError'

  /** The first message of every field, keyed by the dot-notation field path. */
  readonly fieldErrors: Readonly<Record<string, string>>

  /** Every message of every field, as the API sent them. */
  readonly allErrors: Readonly<Record<string, readonly string[]>>

  constructor(body: ErrorBody & { errors: Record<string, string[]> }) {
    super(422, body)
    this.allErrors = body.errors
    this.fieldErrors = Object.fromEntries(
      Object.entries(body.errors).map(([field, messages]) => [field, messages[0] ?? body.message]),
    )
  }
}

/** Pushes each field error of a 422 into a form (`useForm().setFieldError`). */
export function applyFieldErrors(
  error: ApiValidationError,
  setFieldError: (field: string, message: string) => void,
): void {
  for (const [field, message] of Object.entries(error.fieldErrors)) {
    setFieldError(field, message)
  }
}

/** 409: the state of the resource refuses the operation. `reason` names why. */
export class ApiConflictError extends ApiError {
  override readonly name = 'ApiConflictError'

  readonly reason: ConflictReason

  constructor(body: ErrorBody & { reason: ConflictReason }) {
    super(409, body)
    this.reason = body.reason
  }
}

/** The locale key of the message a user sees for a 409 (`errors.reason.<code>`). */
export function conflictMessageKey(error: {
  readonly reason: ConflictReason
}): `errors.reason.${ConflictReason}` {
  return `errors.reason.${error.reason}`
}

/** 404: the resource does not exist, or the caller is not allowed to know it does. */
export class ApiNotFoundError extends ApiError {
  override readonly name = 'ApiNotFoundError'

  constructor(body: ErrorBody | null) {
    super(404, body)
  }
}

/** 429: a rate limit refused the request. `retryAfterSeconds` comes from `Retry-After`. */
export class ApiRateLimitError extends ApiError {
  override readonly name = 'ApiRateLimitError'

  readonly retryAfterSeconds: number | null

  constructor(body: ErrorBody | null, retryAfterSeconds: number | null) {
    super(429, body)
    this.retryAfterSeconds = retryAfterSeconds
  }
}

/**
 * The response did not follow the contract the spec promises (for example a
 * 409 without a known `reason`). This is a server bug, not a user error, so it
 * is loud and never mapped to a friendly message.
 */
export class ApiContractError extends ApiError {
  override readonly name = 'ApiContractError'

  constructor(status: number, body: ErrorBody | null, detail: string) {
    super(status, body, `The API broke its error contract: ${detail}`)
  }
}

/**
 * Thrown before a state-changing request leaves the browser when the
 * `XSRF-TOKEN` cookie is missing. Sanctum would answer 419; failing here names
 * the actual mistake: `GET /sanctum/csrf-cookie` was not called first.
 */
export class MissingCsrfCookieError extends Error {
  override readonly name = 'MissingCsrfCookieError'

  constructor(method: string, url: string) {
    super(
      `${method} ${url} needs the XSRF-TOKEN cookie. Call GET /sanctum/csrf-cookie before a credential post.`,
    )
  }
}
